import { useState } from 'react'
import { AlertTriangle, ArrowLeft, Check, CircleDashed, FileText, Loader2, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  openPortDocument,
  PORT_STEPS,
  useCancelPortRequest,
  usePortRequest,
  type PortDocument,
  type PortRequest,
} from '@/api/portage'
import { formatDay, formatHour, formatNanp } from './portageUtils'

interface Props {
  companyId: number
  transferId: string
  onBack: () => void
  /** Opens the wizard on this request (finish a draft, correct, sign again). */
  onResume: (transfer: PortRequest) => void
  /** Label of the back button: back to the numbers, or back to Callizy on the phone page. */
  backLabel?: string
}

/** The page of one transfer, opened from its line in the numbers table (or the phone page). */
export function PortRequestDetailView({ companyId, transferId, onBack, onResume, backLabel }: Props) {
  const { t } = useTranslation()
  const { data: transfer, isLoading, isError } = usePortRequest(companyId, transferId)

  return (
    <div className="max-w-[720px] mx-auto py-2">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center text-sm text-slate-500 hover:text-slate-800 dark:hover:text-slate-300 mb-6 group transition-colors"
      >
        <ArrowLeft className="h-4 w-4 mr-1 group-hover:-translate-x-1 transition-transform" />
        {backLabel ?? t('manager.numbers.detail.backToNumbers')}
      </button>
      {isError ? (
        <p className="text-sm text-slate-600 dark:text-slate-300 py-8">{t('manager.numbers.port.page.notFound')}</p>
      ) : isLoading || !transfer ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        </div>
      ) : (
        <Detail companyId={companyId} transfer={transfer} onResume={onResume} onBack={onBack} />
      )}
    </div>
  )
}

function Detail({
  companyId,
  transfer,
  onResume,
  onBack,
}: {
  companyId: number
  transfer: PortRequest
  onResume: (t: PortRequest) => void
  onBack: () => void
}) {
  const { t } = useTranslation()
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const cancel = useCancelPortRequest(companyId)
  const [confirmCancel, setConfirmCancel] = useState(false)
  const numbers = transfer.numbers
  const carriers = transfer.carriers.join(', ')
  const date = transfer.confirmed_date
  // The exact time when the operator gave it, in the company's time zone and named.
  const when =
    date && transfer.confirmed_hour && transfer.confirmed_zone
      ? t('manager.numbers.port.detail.atTime', {
          date: formatDay(date, locale),
          time: formatHour(transfer.confirmed_hour, locale),
          zone: t(`manager.numbers.port.detail.zones.${transfer.confirmed_zone}`),
        })
      : formatDay(date, locale)
  const hasMobile = numbers.some((n) => n.line_type === 'cellulaire')
  const active = !['annulee', 'transferee', 'brouillon'].includes(transfer.status)
  const signAgain =
    (transfer.status === 'recue' || transfer.status === 'verifiee') &&
    (transfer.signature_state === 'expiree' || transfer.signature_state === 'champs_changes')

  const handleCancel = async () => {
    try {
      await cancel.mutateAsync({ id: transfer.id })
      toast.success(t('manager.numbers.port.detail.cancelled'))
      onBack()
    } catch {
      toast.error(t('manager.numbers.port.detail.cancelError'))
    }
  }

  const openDoc = async (doc: PortDocument) => {
    try {
      await openPortDocument(companyId, transfer.id, doc.id)
    } catch {
      toast.error(t('manager.numbers.port.errors.generic'))
    }
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white tabular-nums">
            {numbers.length ? formatNanp(numbers[0].phone_number) : t('manager.numbers.port.list.type')}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {numbers.length > 1 && `${t('manager.numbers.port.detail.andOthers', { count: numbers.length - 1 })} · `}
            {carriers ? t('manager.numbers.port.list.from', { carrier: carriers }) : t('manager.numbers.port.list.type')}
          </p>
        </div>
        <StatusBadge status={transfer.status} />
      </div>

      {/* Progress */}
      {transfer.status !== 'brouillon' && transfer.status !== 'annulee' && <Progress transfer={transfer} locale={locale} />}

      {/* Message of the step */}
      <section
        className={cn(
          'rounded-xl border px-5 py-4 space-y-3',
          transfer.status === 'a_corriger' || signAgain
            ? 'border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-900/20'
            : 'border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900',
        )}
      >
        <p className="text-sm text-slate-800 dark:text-slate-100 leading-relaxed">
          {signAgain
            ? t('manager.numbers.port.detail.message.signAgain')
            : t(`manager.numbers.port.detail.message.${transfer.status}`, {
                message: transfer.correction_message,
                date: when,
              })}
        </p>
        {transfer.status === 'date_confirmee' && hasMobile && (
          <p className="text-sm text-slate-800 dark:text-slate-100 leading-relaxed">
            {t('manager.numbers.port.detail.message.mobileText')}
          </p>
        )}
        {(transfer.status === 'brouillon' || transfer.status === 'a_corriger' || signAgain) && (
          <Button type="button" onClick={() => onResume(transfer)} className="bg-blue-500 hover:bg-blue-600 text-white">
            {transfer.status === 'brouillon'
              ? t('manager.numbers.port.detail.finish')
              : transfer.status === 'a_corriger'
                ? t('manager.numbers.port.detail.correct')
                : t('manager.numbers.port.detail.signAgain')}
          </Button>
        )}
      </section>

      {/* Ready for the transfer day */}
      {active && (
        <section className="space-y-4">
          <div>
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">{t('manager.numbers.port.detail.readyTitle')}</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{t('manager.numbers.port.detail.readySubtitle')}</p>
          </div>
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 divide-y divide-slate-100 dark:divide-slate-800">
            <Row
              label={t('manager.numbers.port.detail.date')}
              value={date ? when : t('manager.numbers.port.detail.toConfirm')}
            />
            <Row
              label={t('manager.numbers.port.detail.settings')}
              value={
                transfer.temporary_number
                  ? t('manager.numbers.port.detail.settingsFrom', {
                      number: formatNanp(transfer.temporary_number.phone_number),
                    })
                  : t('manager.numbers.port.detail.settingsDefault')
              }
            />
            {numbers.map((n) => (
              <div key={n.id} className="flex items-center justify-between gap-4 px-4 py-3">
                <span className="text-sm font-medium tabular-nums text-slate-900 dark:text-white">
                  {formatNanp(n.phone_number)}
                  {n.line_type === 'cellulaire' && (
                    <span className="ml-2 text-xs font-normal text-slate-500">{t('manager.numbers.port.numbers.mobile')}</span>
                  )}
                </span>
                <NumberState status={n.status} reason={n.rejection_reason} />
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">{t('manager.numbers.port.checks.keepService')}</p>
        </section>
      )}

      {/* Documents */}
      {(transfer.letter || transfer.invoice) && (
        <section className="space-y-3">
          <h2 className="text-lg font-bold text-slate-900 dark:text-white">{t('manager.numbers.port.detail.documents')}</h2>
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 divide-y divide-slate-100 dark:divide-slate-800">
            {[transfer.letter, transfer.invoice].map(
              (doc) =>
                doc && (
                  <div key={doc.id} className="flex items-center justify-between gap-4 px-4 py-3">
                    <span className="flex items-center gap-2 min-w-0 text-sm text-slate-700 dark:text-slate-200">
                      <FileText className="h-4 w-4 shrink-0 text-slate-400" />
                      <span className="truncate">{t(`manager.numbers.port.detail.doc.${doc.type}`)}</span>
                    </span>
                    <Button type="button" size="sm" variant="outline" onClick={() => void openDoc(doc)}>
                      {t('manager.numbers.port.detail.open')}
                    </Button>
                  </div>
                ),
            )}
          </div>
        </section>
      )}

      {/* Cancel */}
      {transfer.status !== 'annulee' && transfer.status !== 'transferee' && (
        <section className="rounded-lg border border-slate-200 dark:border-slate-800 px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{t('manager.numbers.port.detail.cancelTitle')}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                {transfer.can_cancel
                  ? transfer.cancel_deadline
                    ? t('manager.numbers.port.detail.cancelUntil', { date: formatDay(transfer.cancel_deadline, locale) })
                    : t('manager.numbers.port.detail.cancelRule')
                  : t('manager.numbers.port.detail.cancelTooLate')}
              </p>
            </div>
            {transfer.can_cancel &&
              (confirmCancel ? (
                <div className="flex gap-2">
                  <Button type="button" size="sm" variant="ghost" onClick={() => setConfirmCancel(false)}>
                    {t('manager.numbers.port.detail.keep')}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => void handleCancel()}
                    disabled={cancel.isPending}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    {cancel.isPending && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                    {t('manager.numbers.port.detail.confirmCancel')}
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="border-red-200 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400"
                  onClick={() => setConfirmCancel(true)}
                >
                  {t('manager.numbers.port.detail.cancel')}
                </Button>
              ))}
          </div>
        </section>
      )}
    </div>
  )
}

export function StatusBadge({ status }: { status: PortRequest['status'] }) {
  const { t } = useTranslation()
  const tone =
    status === 'a_corriger'
      ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
      : status === 'transferee'
        ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400'
        : status === 'annulee' || status === 'brouillon'
          ? 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
          : 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300'
  return (
    <span className={cn('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium', tone)}>
      {t(`manager.numbers.port.list.status.${status}`)}
    </span>
  )
}

function Progress({ transfer, locale }: { transfer: PortRequest; locale: string }) {
  const { t } = useTranslation()
  // The steps already done stay done; "Action needed" sits on the next one.
  const reached = PORT_STEPS.filter((s) => !!transfer.steps[s]).length
  const blockedIndex = transfer.status === 'a_corriger' ? reached : -1
  return (
    <ol className="grid grid-cols-1 sm:grid-cols-5 gap-3 sm:gap-2">
      {PORT_STEPS.map((step, i) => {
        const done = i < reached
        const blocked = i === blockedIndex
        const at = transfer.steps[step]
        return (
          <li key={step} className="flex sm:flex-col items-center sm:items-start gap-3 sm:gap-2">
            <div className="flex items-center w-auto sm:w-full">
              <span
                className={cn(
                  'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                  blocked
                    ? 'bg-amber-500 text-white'
                    : done
                      ? 'bg-emerald-500 text-white'
                      : 'bg-slate-100 text-slate-400 dark:bg-slate-800',
                )}
              >
                {blocked ? <AlertTriangle className="h-3.5 w-3.5" /> : done ? <Check className="h-3.5 w-3.5" /> : i + 1}
              </span>
              {i < PORT_STEPS.length - 1 && (
                <span className={cn('hidden sm:block flex-1 h-px mx-2', done ? 'bg-emerald-300' : 'bg-slate-200 dark:bg-slate-700')} />
              )}
            </div>
            <div>
              <p className={cn('text-sm font-medium', done || blocked ? 'text-slate-900 dark:text-white' : 'text-slate-400')}>
                {blocked ? t('manager.numbers.port.list.status.a_corriger') : t(`manager.numbers.port.detail.step.${step}`)}
              </p>
              {at && !blocked && (
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {new Date(at).toLocaleDateString(locale === 'fr' ? 'fr-CA' : 'en-CA', { day: 'numeric', month: 'short' })}
                </p>
              )}
            </div>
          </li>
        )
      })}
    </ol>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-4 px-4 py-3">
      <span className="text-sm text-slate-500 dark:text-slate-400">{label}</span>
      <span className="text-sm font-medium text-slate-900 dark:text-slate-100 sm:text-right">{value}</span>
    </div>
  )
}

function NumberState({ status, reason }: { status: PortRequest['numbers'][number]['status']; reason: string }) {
  const { t } = useTranslation()
  if (status === 'rejete') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-red-600 dark:text-red-400 text-right">
        <X className="h-3.5 w-3.5 shrink-0" />
        {reason ? t('manager.numbers.port.detail.rejectedReason', { reason }) : t('manager.numbers.port.detail.rejected')}
      </span>
    )
  }
  if (status === 'branche' || status === 'transfere') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-400">
        <Check className="h-3.5 w-3.5" />
        {t('manager.numbers.port.detail.ready')}
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
      <CircleDashed className="h-3.5 w-3.5" />
      {t('manager.numbers.port.detail.waiting')}
    </span>
  )
}
